import { PageLayout } from '@/components/layouts/PageLayout.tsx';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store.ts';
import Title from 'antd/es/typography/Title';
import React from 'react';
import { Card, Col, Flex, Row, Typography } from 'antd';
import { Link } from 'react-router';
import type { MenuDataProps } from '@/components/AntdForm/antd-components.def.ts';
import { useVisibleNavMenuData } from '@/components/layouts/nav-menu-visibility.ts';
import { Permission } from '@/features/auth/auth.def.ts';
import { useAuth } from '@/hooks/useAuth.ts';

const ItemLink: React.FC<{ item: MenuDataProps }> = ({ item }) =>
  item.url ? (
    <Link to={item.url} target={item.target}>
      {item.label}
    </Link>
  ) : (
    <>{item.label}</>
  );

/**
 * One card per visible nav section, listing the pages inside it. Built from the same filtered tree
 * as the nav menu, so it never offers a page the user cannot open.
 */
const PortalGuide: React.FC = () => {
  const sections = useVisibleNavMenuData();

  return (
    <section aria-labelledby="portal-guide-heading">
      <Title level={4} id="portal-guide-heading">
        Where to go
      </Title>
      <Row gutter={[16, 16]}>
        {sections.map((section) => (
          <Col key={section.key} xs={24} md={12} xl={8}>
            <Card
              size="small"
              title={
                <Flex align="center" gap={8}>
                  {section.icon}
                  <ItemLink item={section} />
                </Flex>
              }
              className="h-full"
              data-testid={`portal-guide-${section.key}`}
            >
              {section.description ? (
                <Typography.Paragraph type="secondary">{section.description}</Typography.Paragraph>
              ) : null}
              {section.children?.length ? (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {section.children.map((child) => (
                    <li key={child.key}>
                      <ItemLink item={child} />
                      {child.description ? (
                        <Typography.Text type="secondary" className="block text-xs">
                          {child.description}
                        </Typography.Text>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          </Col>
        ))}
      </Row>
    </section>
  );
};

export const HomePage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { checkHasAllPermissions } = useAuth();
  // Users without portal access keep the plain greeting; the nav shows them only developer tools.
  const hasPortalAccess = checkHasAllPermissions([Permission.PortalAccess]);

  // Get the current hour (0-23)
  const currentHour = new Date().getHours();

  let greeting;
  if (currentHour >= 5 && currentHour < 12) {
    greeting = 'Good morning';
  } else if (currentHour >= 12 && currentHour < 18) {
    greeting = 'Good afternoon';
  } else {
    greeting = 'Good evening';
  }

  return (
    <PageLayout>
      <Flex vertical gap={24}>
        {/* Level 3 stays: home-page.spec.ts and portal-access.spec.ts read the greeting by it. */}
        <Title level={3}>
          {greeting}, {user?.name.toUpperCase()}
        </Title>
        {hasPortalAccess ? <PortalGuide /> : null}
      </Flex>
    </PageLayout>
  );
};
